<template>
  <div class="JOB-LIST">
    <transition :name="transitionDirection">
      <div class="JOB-LIST-CONTAINER">
        <transition name="VERTICAL" mode="out-in">
          <transition-group
              v-if="count || isFetching"
              v-infinite-scroll="{
                offset: 666,
                promise: hasMore && !isFetching && loadMoreJobs
              }"
              name="SWIPE"
              @before-leave="onListItemBeforeLeave"
              tag="div"
              class="d-flex w-100 fd-column ov-y-scroll">
            <div
                v-for="(listItem, order_hash) in list"
                :key="order_hash"
                @click="clickOnMessage(listItem.message_id, listItem.order_hash)"
            >
              <div
                  class="cur-pointer d-flex jc-between bd-b-gray50"
                  :class="[jobId===listItem.order_hash?'bg-gray30':'']"
              >
                <avatar
                    :name="customerName(listItem)"
                    :id="listItem.sender"
                    :size="35"
                    class="flex-0-0 as-center m-x-2"
                    selenium="message-avatar"/>
                <div class="flex-1-1 ov-hidden">
                  <div class="p-t-2 text-bold">{{ customerName(listItem) }}</div>
                  <div class="p-b-2 MESSAGE"> {{ listItem.message }}</div>
                </div>
                <div>
                  <div class="p-r-2 p-t-2 flex-0-0"> {{ formatDate(listItem.time_created) }}</div>
                  <div v-if="checkUnread(listItem.order_hash)" class="DOT">New</div>
                </div>
              </div>
            </div>
            <template v-if="isFetching">
              <job-list-item-placeholder
                  v-for="(index) in 3"
                  :key="`placeholder-${index}`"
                  class="LIST-ITEM"
              />
            </template>
          </transition-group>
          <job-list-placeholder v-else-if="isUninitialized" />
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import jobParams from '@/mixins/job-params';
import job from '@/mixins/job';
import { format, isToday, differenceInDays } from 'date-fns';
import JobListItemPlaceholder from '@/components/JobList/JobListItemPlaceholder';
import JobListPlaceholder from '@/components/JobList/JobListPlaceholder';

export default {
  name: 'message-list',
  components: {
    JobListItemPlaceholder,
    JobListPlaceholder,
  },
  mixins: [
    jobParams,
    job,
  ],
  data () {
    return {
      transitionDirection: 'NONE',
      status: this.$options.REQUEST_LIFECYCLE.UNINITIALIZED,
    };
  },
  computed: {
    ...mapGetters('account/pro/info', [
      'serviceProviderId',
    ]),
    ...mapGetters('account/pro/inbox', ['getInbox', 'getTotal', 'getCount', 'hasUnreadMessages', 'unreadMessages']),
    isUninitialized () {
      return this.status === this.$options.REQUEST_LIFECYCLE.UNINITIALIZED;
    },
    isFetching () {
      return this.status === this.$options.REQUEST_LIFECYCLE.FETCHING;
    },
    hasMore () {
      return this.list.length < this.total;
    },
    total () {
      return this.getTotal;
    },
    count () {
      return this.getCount;
    },
    list () {
      return this.getInbox;
    },
  },
  watch: {
    hasUnreadMessages: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal !== oldVal && newVal) {
          this.updateInbox();
        }
      },
    },
  },
  mounted () {
    this.loadJobs(this.jobId);
    if (!this.isFulfilled || !this.count) {
      this.fetchMessages();
    }
  },
  methods: {
    ...mapActions('account/pro/inbox', ['fetchInbox', 'updateInbox', 'removeFromUnread']),
    clickOnMessage (message_id, order_hash) {
      if (this.$route.params.jobId !== order_hash) {
        this.$emit('clickOnMessage', order_hash);
      }
      this.$root.$emit('readingMessage');
      this.loadJobs(order_hash);
      this.removeFromUnread({ message_id, order_hash });
    },
    formatDate (date) {
      const dayToCheck = new Date(date);
      if (differenceInDays(new Date(), dayToCheck) >= 5) {
        return format(dayToCheck, 'MMM dd');
      }
      return isToday(dayToCheck) ? 'Today' : format(dayToCheck, 'cccc');
    },
    customerName (message) {
      if (message.sender_type === 'pro') {
        return message.recipient_full_name;
      }
      return message.sender_full_name;
    },
    async fetchMessages (params) {
      if (this.serviceProviderId) {
        this.status = this.$options.REQUEST_LIFECYCLE.FETCHING;
        try {
          await this.fetchInbox({ params, reason: 'list' });
        } catch (e) {
          console.error(e); // eslint-disable-line no-console
        } finally {
          if (this.list.length) {
            this.$emit('updateHasMessages', true);
          }
          this.status = this.$options.REQUEST_LIFECYCLE.FULFILLED;
        }
      }
    },
    async loadJobs (ids) {
      await this.fetchWorkOrderMatches({
        includeAppointments: true,
        params: {
          order_hash: ids,
          service_provider_id: this.serviceProviderId,
        },
      });
    },
    async loadMoreJobs () {
      if (this.hasMore && !this.isFetching) {
        await this.fetchMessages({ from: this.list.length });
      }
    },
    clearJobs () {
      this.resetWorkOrderMatches({ jobsStatusAlias: this.jobsStatusAlias });
    },
    onListItemBeforeLeave (el) {
      const { top: parentTop } = el.parentElement.getBoundingClientRect();
      const { top } = el.getBoundingClientRect();

      el.style.top = `${top - parentTop}px`;

      global.requestAnimationFrame(() => {
        el.style.position = 'absolute';
      });
    },
    checkUnread (order_hash) {
      return this.unreadMessages.some(message => message.order_hash === order_hash);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";
  @import "~@/views/Dashboard/constants";

  .JOB-LIST {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  .JOB-LIST-CONTAINER {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 90vw;
  }

  .MESSAGE {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .LEFT-enter-active,
  .RIGHT-enter-active,
  .LEFT-leave-active,
  .RIGHT-leave-active {
    @include absolute(0, 0, 0, 0);
    transform: none;
    will-change: transform;
    transition: transform $transition-duration;

    &.LEFT-enter,
    &.RIGHT-leave-to {
      transform: translateX(-100%);
    }

    &.LEFT-leave-to,
    &.RIGHT-enter {
      transform: translateX(100%);
    }
  }

  .VERTICAL-enter-active,
  .VERTICAL-leave-active {
    opacity: 1;
    transform: none;
    will-change: transform, opacity;
    transition: transform $transition-half-duration,
      opacity $transition-half-duration;

    &.VERTICAL-enter,
    &.VERTICAL-leave-to {
      opacity: 0;
      transform: translateY(30px);
    }
  }

  .LIST-ITEM {
    opacity: 1;
    transform: none;
    transition: all $transition-duration;

    &.SWIPE-enter,
    &.SWIPE-leave-to {
      opacity: 0;
    }

    &.SWIPE-enter {
      transform: translateY(30px);
    }

    &.SWIPE-leave-to {
      transform: translateX(100%);
    }
  }
  .DOT {
      color: white;
      background-color: #286FAD;
      border-radius: 16px;
      max-width: min-content;
      padding: 0 8px;
  }
</style>
